import type { JSONContent } from '@tiptap/core';
import { generateHTML, generateJSON } from '@tiptap/html';
import { investmentCellRichExtensions } from '@/editor/investmentCellRichExtensions';
import type { InvestmentRow } from '@/types';

/**
 * Platte tekst uit een TipTap-tableCell als JSONContent (autosave naar blocks.rows[].title/kolommen).
 * - Meerdere paragrafen (Enter): gescheiden door \\n
 * - Shift+Enter / hardBreak binnen een paragraaf: ook \\n (soft line break)
 */
export function investmentCellPlainText(cell: JSONContent): string {
  if (!cell.content?.length) return '';
  const blocks = cell.content ?? [];
  const perBlock = blocks.map((block) => {
    const inner: string[] = [];
    const walk = (n: JSONContent) => {
      if (n.type === 'text') inner.push(n.text ?? '');
      else if (n.type === 'hardBreak') inner.push('\n');
      else if (n.content) n.content.forEach(walk);
    };
    walk(block);
    return inner.join('');
  });
  return perBlock.join('\n');
}

const RICH_MARK_TYPES = new Set(['bold', 'italic', 'underline', 'strike', 'link', 'textStyle']);

function nodeHasRichMarks(n: JSONContent): boolean {
  if (n.marks?.some((m) => RICH_MARK_TYPES.has(m.type ?? ''))) return true;
  if (n.type === 'bulletList' || n.type === 'orderedList') return true;
  if (n.content) return n.content.some(nodeHasRichMarks);
  return false;
}

function nodeHasHardBreak(n: JSONContent): boolean {
  if (n.type === 'hardBreak') return true;
  if (n.content) return n.content.some(nodeHasHardBreak);
  return false;
}

/** True als de omschrijvingscel vet/cursief/lijsten bevat — dan slaan we ook `titleHtml` op. */
export function investmentCellHasRichMarks(cell: JSONContent | undefined): boolean {
  if (!cell?.content?.length) return false;
  return cell.content.some(nodeHasRichMarks);
}

/** True als HTML nodig is om visuele structuur zoals Enter-spacing te bewaren. */
export function investmentCellShouldStoreHtml(cell: JSONContent | undefined): boolean {
  if (!cell?.content?.length) return false;
  return investmentCellHasRichMarks(cell) || cell.content.length > 1 || cell.content.some(nodeHasHardBreak);
}

/** HTML voor publieke weergave; alleen blokken binnen een tableCell/tableHeader. */
export function investmentFirstCellRichHtml(cell: JSONContent | undefined): string | undefined {
  if (!cell || (cell.type !== 'tableCell' && cell.type !== 'tableHeader')) return undefined;
  const inner = cell.content ?? [];
  if (inner.length === 0) return undefined;
  try {
    const doc = { type: 'doc', content: inner } as JSONContent;
    const html = generateHTML(doc, investmentCellRichExtensions).trim();
    if (!html || html === '<p></p>' || html === '<p></p><p></p>') return undefined;
    return html;
  } catch {
    return undefined;
  }
}

/**
 * Bouwt tableCell-inhoud voor de omschrijving-kolom vanuit opgeslagen string (roundtrip met investmentCellPlainText).
 */
export function investmentDescriptionToCellParagraphs(title: string | undefined): JSONContent[] {
  const t = title ?? '';
  if (t === '') return [{ type: 'paragraph' }];
  return t.split(/\r?\n/).map((line) => ({
    type: 'paragraph',
    content: line ? [{ type: 'text', text: line }] : [],
  }));
}

/**
 * Roundtrip: `title` (plain) + optioneel `titleHtml` terug naar TipTap-celnodes.
 */
export function investmentTitleCellContent(row: Pick<InvestmentRow, 'title' | 'titleHtml'>): JSONContent[] {
  const html = row.titleHtml?.trim();
  if (html) {
    try {
      const wrapped = html.startsWith('<') ? html : `<p>${html}</p>`;
      const json = generateJSON(wrapped, investmentCellRichExtensions) as JSONContent;
      const nodes = json?.content;
      if (nodes?.length) return nodes;
    } catch {
      /* fall back */
    }
  }
  return investmentDescriptionToCellParagraphs(row.title);
}

/** Empty variant descriptions use the product name, including empty richtext paragraphs. */
export function investmentProductDescriptionContent(title: string, descriptionHtml?: string): JSONContent[] {
  const content = investmentTitleCellContent({ title, titleHtml: descriptionHtml });
  return investmentCellPlainText({ type: 'tableCell', content }).trim()
    ? content
    : investmentDescriptionToCellParagraphs(title);
}

const ALLOWED_TAGS = new Set([
  'P',
  'BR',
  'STRONG',
  'B',
  'EM',
  'I',
  'U',
  'S',
  'STRIKE',
  'DEL',
  'UL',
  'OL',
  'LI',
  'SPAN',
  'DIV',
]);

const ALLOWED_STYLE_PROPS = new Set([
  'color',
  'background-color',
  'text-align',
  'font-weight',
  'font-style',
  'text-decoration',
]);

function sanitizeStyleAttribute(value: string): string {
  return value
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const [rawProp, ...rawValueParts] = part.split(':');
      const prop = rawProp?.trim().toLowerCase();
      const propValue = rawValueParts.join(':').trim();
      if (!prop || !propValue || !ALLOWED_STYLE_PROPS.has(prop)) return '';
      if (/url\s*\(|expression\s*\(|javascript:/i.test(propValue)) return '';
      return `${prop}: ${propValue}`;
    })
    .filter(Boolean)
    .join('; ');
}

/** Beperkte HTML voor omschrijvingscel (geen script/event handlers). */
export function sanitizeInvestmentDescriptionHtml(html: string): string {
  const t = html.trim();
  if (!t) return '';

  if (typeof document === 'undefined') {
    return t.replace(/<\/?(?:script|style|iframe|object|embed|form)[^>]*>/gi, '').replace(/\son\w+\s*=/gi, '');
  }

  const tpl = document.createElement('template');
  tpl.innerHTML = t;

  const stripDangerousAttrs = (el: Element): void => {
    [...el.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name.startsWith('on')) el.removeAttribute(attr.name);
      if (name === 'href' || name === 'src') el.removeAttribute(attr.name);
      if (name === 'style') {
        const safeStyle = sanitizeStyleAttribute(attr.value);
        if (safeStyle) el.setAttribute('style', safeStyle);
        else el.removeAttribute(attr.name);
      }
      if (
        attr.value.toLowerCase().includes('javascript:') ||
        attr.value.toLowerCase().includes('data:')
      ) {
        el.removeAttribute(attr.name);
      }
    });
  };

  const walk = (parent: ParentNode): void => {
    const children = [...parent.childNodes];
    for (const node of children) {
      if (node.nodeType === Node.TEXT_NODE || node.nodeType === Node.COMMENT_NODE) continue;
      if (node.nodeType !== Node.ELEMENT_NODE) {
        node.remove();
        continue;
      }
      const el = node as HTMLElement;
      const tag = el.tagName;
      if (!ALLOWED_TAGS.has(tag)) {
        while (el.firstChild) parent.insertBefore(el.firstChild, el);
        parent.removeChild(el);
        continue;
      }
      stripDangerousAttrs(el);
      if (el.childNodes.length) walk(el);
    }
  };

  walk(tpl.content);

  const container = document.createElement('div');
  container.appendChild(tpl.content);

  return container.innerHTML;
}
